import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { id: "techstack", label: "TechStack" },
  { id: "experience", label: "Experience" },
  { id: "opensource", label: "OpenSource" },
  { id: "projects", label: "Projects" },
  { id: "profiles", label: "Profiles" },
  { id: "contact", label: "LetsConnect" },
];

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);

  // Track scroll for shrink effect
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track active section via IntersectionObserver
  useEffect(() => {
    const observers = [];
    navLinks.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActive(id); },
        { threshold: 0.35 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  return (
    <>
      {/* ── Main navbar ── */}
      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        className="fixed top-0 left-0 right-0 z-50"
        style={{ pointerEvents: "auto" }}
      >
        <div
          className="mx-auto transition-all duration-500"
          style={{
            maxWidth: scrolled ? "820px" : "100%",
            padding: scrolled ? "10px 16px" : "0 24px",
            marginTop: scrolled ? "12px" : "0",
          }}
        >
          <div
            className="flex justify-between items-center transition-all duration-500"
            style={{
              background: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(20px) saturate(160%)",
              WebkitBackdropFilter: "blur(20px) saturate(160%)",
              border: scrolled
                ? "2px solid rgba(0,0,0,1)"
                : "2px solid rgba(0,0,0,0.12)",
              borderRadius: scrolled ? "999px" : "0px",
              padding: scrolled ? "8px 8px 8px 20px" : "20px 32px",
              boxShadow: scrolled
                ? "4px 4px 0px 0px rgba(0,0,0,1)"
                : "none",
            }}
          >
            {/* ── Logo ── */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.96 }}
              className="flex items-center gap-3 cursor-pointer select-none"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              <div
                className="flex items-center justify-center font-bold text-white"
                style={{
                  width: scrolled ? "36px" : "44px",
                  height: scrolled ? "36px" : "44px",
                  borderRadius: "50%",
                  background: "#7c3aed",
                  border: "2px solid black",
                  fontSize: scrolled ? "15px" : "18px",
                  transition: "all 0.4s ease",
                  flexShrink: 0,
                }}
              >
                K
              </div>
              <AnimatePresence>
                {!scrolled && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: "auto" }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.3 }}
                    className="text-gray-700 font-mono text-xs tracking-widest overflow-hidden whitespace-nowrap"
                    style={{ letterSpacing: "0.2em" }}
                  >
                    krishna.dev
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>

            {/* ── Desktop Links ── */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link, i) => {
                const isActive = active === link.id;
                const isHovered = hoveredId === link.id;
                return (
                  <motion.a
                    key={link.id}
                    href={`#${link.id}`}
                    onClick={(e) => { e.preventDefault(); scrollToSection(link.id); }}
                    onHoverStart={() => setHoveredId(link.id)}
                    onHoverEnd={() => setHoveredId(null)}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 + i * 0.07, duration: 0.4 }}
                    className="relative px-3 py-2 rounded-xl text-sm font-mono transition-colors duration-200"
                    style={{
                      color: isActive
                        ? "#6d28d9"
                        : isHovered
                          ? "#7c3aed"
                          : "rgba(0,0,0,0.6)",
                      textDecoration: "none",
                      letterSpacing: "0.01em",
                    }}
                  >
                    {/* Active / hover background pill */}
                    <AnimatePresence>
                      {(isActive || isHovered) && (
                        <motion.span
                          layoutId="nav-pill"
                          initial={{ opacity: 0, scale: 0.85 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.85 }}
                          transition={{ type: "spring", stiffness: 380, damping: 28 }}
                          className="absolute inset-0 rounded-xl"
                          style={{
                            background: isActive
                              ? "rgba(124,58,237,0.12)"
                              : "rgba(124,58,237,0.06)",
                            border: isActive
                              ? "1.5px solid rgba(0,0,0,0.9)"
                              : "1.5px solid rgba(0,0,0,0.15)",
                          }}
                        />
                      )}
                    </AnimatePresence>

                    {/* Label */}
                    <span className="relative z-10 flex items-center gap-0.5">
                      <span style={{ color: "rgba(124,58,237,0.65)", fontSize: "11px" }}>&lt;/</span>
                      {link.label}
                      <span style={{ color: "rgba(124,58,237,0.65)", fontSize: "11px" }}>&gt;</span>
                    </span>

                    {/* Active dot */}
                    {isActive && (
                      <motion.span
                        layoutId="active-dot"
                        className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
                        style={{ background: "#7c3aed" }}
                      />
                    )}
                  </motion.a>
                );
              })}
            </div>

            {/* ── Mobile toggle ── */}
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.92 }}
              className="md:hidden flex items-center justify-center w-9 h-9 rounded-xl text-black"
              style={{
                background: isOpen ? "rgba(124,58,237,0.12)" : "rgba(0,0,0,0.03)",
                border: "2px solid black",
              }}
              aria-label="Toggle menu"
            >
              <motion.div
                animate={isOpen ? "open" : "closed"}
                className="flex flex-col gap-1 w-4"
              >
                <motion.span
                  variants={{ closed: { rotate: 0, y: 0 }, open: { rotate: 45, y: 6 } }}
                  transition={{ duration: 0.25 }}
                  className="block h-px bg-current rounded"
                />
                <motion.span
                  variants={{ closed: { opacity: 1 }, open: { opacity: 0 } }}
                  transition={{ duration: 0.2 }}
                  className="block h-px bg-current rounded"
                />
                <motion.span
                  variants={{ closed: { rotate: 0, y: 0 }, open: { rotate: -45, y: -6 } }}
                  transition={{ duration: 0.25 }}
                  className="block h-px bg-current rounded"
                />
              </motion.div>
            </motion.button>
          </div>
        </div>

        {/* ── Mobile Menu ── */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -12, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.97 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="md:hidden mx-4 mt-2 rounded-2xl overflow-hidden"
              style={{
                background: "rgba(255, 255, 255, 0.97)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                border: "2px solid black",
                boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)",
              }}
            >
              <div className="p-3 flex flex-col gap-1">
                {navLinks.map((link, i) => (
                  <motion.a
                    key={link.id}
                    href={`#${link.id}`}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05, duration: 0.3 }}
                    onClick={(e) => {
                      e.preventDefault();
                      setIsOpen(false);
                      setTimeout(() => scrollToSection(link.id), 120);
                    }}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-mono transition-all duration-200"
                    style={{
                      color: active === link.id ? "#6d28d9" : "rgba(0,0,0,0.65)",
                      background: active === link.id
                        ? "rgba(124,58,237,0.1)"
                        : "transparent",
                      border: active === link.id
                        ? "1.5px solid rgba(0,0,0,0.9)"
                        : "1.5px solid transparent",
                      textDecoration: "none",
                    }}
                  >
                    <span style={{ color: "rgba(124,58,237,0.6)", fontSize: "10px" }}>&lt;/</span>
                    {link.label}
                    <span style={{ color: "rgba(124,58,237,0.6)", fontSize: "10px" }}>&gt;</span>
                    {active === link.id && (
                      <span
                        className="ml-auto w-1.5 h-1.5 rounded-full"
                        style={{ background: "#7c3aed" }}
                      />
                    )}
                  </motion.a>
                ))}
              </div>

              {/* Bottom divider + hint */}
              <div
                className="px-4 py-2.5 flex items-center gap-2"
                style={{ borderTop: "1.5px solid rgba(0,0,0,0.1)" }}
              >
                <span className="text-xs font-mono text-purple-600/50 tracking-widest">
                  krishna.dev
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
}